import { movies } from "@/mocks/movies";
import type { Movie, MovieType } from "@/types/movie";

export const allMovies = movies;

export const genreOptions = [
  "剧情",
  "喜剧",
  "动作",
  "科幻",
  "悬疑",
  "爱情",
  "犯罪",
  "奇幻",
  "冒险",
  "历史",
  "动画",
  "惊悚",
  "青春",
  "医疗",
  "商战",
  "战争",
  "年代",
];

export const regionOptions = ["中国", "美国", "韩国", "日本", "英国"];

export const yearOptions = [2026, 2025];

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return movies.find((m) => m.id === id);
}

export function getByType(type: MovieType): Movie[] {
  return movies.filter((m) => m.type === type);
}

export function sortMovies(list: Movie[], sort: "latest" | "hot"): Movie[] {
  const copied = [...list];
  if (sort === "hot") {
    return copied.sort((a, b) => b.rating - a.rating);
  }
  return copied.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

export function getLatest(limit = 10, type?: MovieType): Movie[] {
  const base = type ? getByType(type) : movies;
  return sortMovies(base, "latest").slice(0, limit);
}

export function getHot(limit = 5): Movie[] {
  return sortMovies(movies, "hot").slice(0, limit);
}

export function getRelated(movie: Movie, limit = 4): Movie[] {
  return movies
    .filter((m) => m.id !== movie.id && m.type === movie.type)
    .map((m) => ({
      movie: m,
      score: m.genres.filter((g) => movie.genres.includes(g)).length,
    }))
    .sort((a, b) => b.score - a.score || b.movie.rating - a.movie.rating)
    .slice(0, limit)
    .map((item) => item.movie);
}

export interface FilterState {
  genres: string[];
  regions: string[];
  years: number[];
  sort: "latest" | "hot";
}

export function filterMovies(list: Movie[], filter: FilterState): Movie[] {
  const filtered = list.filter((m) => {
    const genreOk =
      filter.genres.length === 0 ||
      m.genres.some((g) => filter.genres.includes(g));
    const regionOk =
      filter.regions.length === 0 || filter.regions.includes(m.region);
    const yearOk =
      filter.years.length === 0 || filter.years.includes(m.year);
    return genreOk && regionOk && yearOk;
  });
  return sortMovies(filtered, filter.sort);
}

export function searchMovies(
  keyword: string,
  type: MovieType | "all" = "all"
): Movie[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return movies.filter((m) => {
    const typeOk = type === "all" || m.type === type;
    if (!typeOk) return false;
    return (
      m.title.toLowerCase().includes(kw) ||
      m.genres.some((g) => g.toLowerCase().includes(kw)) ||
      m.region.toLowerCase().includes(kw) ||
      m.director.toLowerCase().includes(kw) ||
      m.actors.some((a) => a.toLowerCase().includes(kw)) ||
      m.tags.some((t) => t.toLowerCase().includes(kw)) ||
      m.summary.toLowerCase().includes(kw)
    );
  });
}

export function formatDate(date: string): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function typeLabel(type: MovieType): string {
  return type === "movie" ? "电影" : "电视剧";
}